/**
 * PLAT-11072 DS10 — the reel's rows in the order the creator sees them: the
 * Sections that are on, in timeline order, with the ones that are off sorted
 * back into the places they were turned off from.
 *
 * The list is what the panel numbers, drags, groups into chapters and renders;
 * the timeline is only the rows that are on. Keeping the two apart is what lets
 * an off row behave exactly like a live one without any of the timeline code
 * learning that the feature exists (DS1).
 *
 * Anchors resolve through each other, so a run of Sections turned off one after
 * another comes back in order. An anchor that no longer names anything in the
 * list — the row it sat after was removed — puts its row at the end rather than
 * dropping it (DS3).
 */

/** A row that is off, and the row it sits after; `''`/absent opens the list. */
export type AnchoredRow<T> = { row: T; afterSectionId?: string };

export function orderReelRows<T extends { id: string }>(
  live: readonly T[],
  off: readonly AnchoredRow<T>[],
): T[] {
  if (off.length === 0) return [...live];

  // `''` is the top of the list — the same reading `ReelChapter.afterSectionId`
  // takes for the gap with nothing above it.
  const byAnchor = new Map<string, T[]>();
  for (const { row, afterSectionId } of off) {
    const anchor = afterSectionId ?? '';
    const bucket = byAnchor.get(anchor);
    if (bucket) bucket.push(row);
    else byAnchor.set(anchor, [row]);
  }

  const ordered: T[] = [];
  const emitted = new Set<string>();

  // Whatever sits behind `id`, and whatever sits behind those. `emitted` is what
  // terminates a cycle — two entries anchored to each other are a state nothing
  // writes, but a stored file is not a thing to trust on that.
  const emitAfter = (id: string): void => {
    for (const row of byAnchor.get(id) ?? []) {
      if (emitted.has(row.id)) continue;
      emitted.add(row.id);
      ordered.push(row);
      emitAfter(row.id);
    }
  };

  emitAfter('');
  for (const row of live) {
    ordered.push(row);
    emitAfter(row.id);
  }

  // Anchored to a row that has left the reel: kept, at the end (DS3).
  for (const { row } of off) {
    if (emitted.has(row.id)) continue;
    emitted.add(row.id);
    ordered.push(row);
  }

  return ordered;
}
